import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import MemoryApi from '../../service/Api/memoryApi';

const initialState = {
    memories: {},
    loading: true,
    error: null,
    updating: false,
    updateSuccess: false,
    editingSection: null,
    isEnabled: true,
    isRegionRestricted: false,
    hasUnsavedChanges: false,
    pendingNavigation: null
};

export const checkMemoryRegion = createAsyncThunk('memory/checkMemoryRegion', async (_, { rejectWithValue }) => {
    try {
        const data = await MemoryApi.checkRegion();
        return data;
    } catch (error) {
        return rejectWithValue(false);
    }
});

export const fetchMemories = createAsyncThunk('memory/fetchMemories', async (_, { rejectWithValue }) => {
    try {
        const data = await MemoryApi.getMemories();
        return data?.data?.data || { memories: {}, isMemoryEnabled: true };
    } catch (error) {
        return rejectWithValue(error?.response?.status || 'unknown');
    }
});

export const updateMemory = createAsyncThunk('memory/updateMemory', async ({ memoryId, content }, { rejectWithValue }) => {
    try {
        const data = await MemoryApi.updateMemory(memoryId, content);
        return data.data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteMemory = createAsyncThunk('memory/deleteMemory', async (memoryId, { rejectWithValue }) => {
    try {
        await MemoryApi.deleteMemory(memoryId);
        return memoryId;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteAllMemories = createAsyncThunk('memory/deleteAllMemories', async (_, { rejectWithValue }) => {
    try {
        await MemoryApi.deleteAllMemories();
        return true;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const toggleMemory = createAsyncThunk('memory/toggleMemory', async (enabled, { rejectWithValue }) => {
    try {
        const data = await MemoryApi.toggleMemory(enabled);
        return data.data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

const memorySlice = createSlice({
    name: 'memory',
    initialState,
    reducers: {
        setEditingSection: (state, { payload }) => {
            state.editingSection = payload;
        },
        clearUpdateSuccess: (state) => {
            state.updateSuccess = false;
        },
        clearMemoryError: (state) => {
            state.error = null;
        },
        setHasUnsavedChanges: (state, { payload }) => {
            state.hasUnsavedChanges = payload;
        },
        setPendingNavigation: (state, { payload }) => {
            state.pendingNavigation = payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchMemories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMemories.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.memories = payload?.memories || {};
                state.isEnabled = payload?.isMemoryEnabled !== false;
            })
            .addCase(fetchMemories.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            })
            .addCase(updateMemory.pending, (state) => {
                state.updating = true;
                state.updateSuccess = false;
            })
            .addCase(updateMemory.fulfilled, (state, { payload }) => {
                state.updating = false;
                state.updateSuccess = true;
                const bucket = payload.bucket;
                if (state.memories[bucket]) {
                    const index = state.memories[bucket].findIndex((m) => m._id === payload._id);
                    if (index !== -1) {
                        state.memories[bucket][index] = payload;
                    }
                }
            })
            .addCase(updateMemory.rejected, (state, { payload }) => {
                state.updating = false;
                state.error = payload;
            })
            .addCase(deleteMemory.fulfilled, (state, { payload: memoryId }) => {
                for (const bucket of Object.keys(state.memories)) {
                    state.memories[bucket] = state.memories[bucket].filter((m) => m._id !== memoryId);
                }
            })
            .addCase(toggleMemory.fulfilled, (state, { payload }) => {
                state.isEnabled = payload.isMemoryEnabled;
            })
            .addCase(deleteAllMemories.fulfilled, (state) => {
                state.memories = {};
                state.editingSection = null;
            })
            .addCase(checkMemoryRegion.fulfilled, (state, { payload }) => {
                state.isRegionRestricted = payload.isMemoryRestricted;
            });
    }
});

export const { setEditingSection, clearUpdateSuccess, clearMemoryError, setHasUnsavedChanges, setPendingNavigation } = memorySlice.actions;

export const getMemoryState = (state) => state.memory;
export const getMemories = (state) => state.memory.memories;
export const getMemoryLoading = (state) => state.memory.loading;
export const getMemoryUpdating = (state) => state.memory.updating;
export const getMemoryUpdateSuccess = (state) => state.memory.updateSuccess;
export const getEditingSection = (state) => state.memory.editingSection;
export const getIsMemoryEnabled = (state) => state.memory.isEnabled;
export const getIsRegionRestricted = (state) => state.memory.isRegionRestricted;
export const getHasUnsavedChanges = (state) => state.memory.hasUnsavedChanges;
export const getPendingNavigation = (state) => state.memory.pendingNavigation;

export const memoryReducer = memorySlice.reducer;
